// ============================================================
//  src/admin/portfolio/PortfolioAdmin.jsx
// ============================================================

import React, { useState, useMemo, useEffect, useCallback } from "react";
import PortfolioCard from "./PortfolioCard";
import PortfolioForm from "./PortfolioForm";

import {
  apiGetPortfolio,
  apiDeletePortfolio,
  CATEGORIES,
  getStats,
  getCategoryCounts,
} from "./portfolioData";

import "./PortfolioAdmin.css";

const useToast = () => {
  const [toast, setToast] = useState(null);
  const show = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  }, []);
  return { toast, show };
};

const PortfolioAdmin = () => {
  const [items,          setItems]          = useState([]);
  const [stats,          setStats]          = useState({ total: 0, featured: 0, images: 0, videos: 0 });
  const [catCounts,      setCatCounts]      = useState({});
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeType,     setActiveType]     = useState("all");
  const [search,         setSearch]         = useState("");
  const [formOpen,       setFormOpen]       = useState(false);
  const [editItem,       setEditItem]       = useState(null);
  const [loading,        setLoading]        = useState(true);

  const { toast, show: showToast } = useToast();

  const refreshDerivedData = useCallback((portfolioItems) => {
    const safe = Array.isArray(portfolioItems) ? portfolioItems : [];
    setStats(getStats(safe));
    setCatCounts(getCategoryCounts(safe));
  }, []);

  const loadPortfolio = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGetPortfolio();
      const safe = Array.isArray(data) ? data : [];
      setItems(safe);
      refreshDerivedData(safe);
    } catch (err) {
      console.error("Failed to load portfolio:", err);
      // Show the real error message from apiFetch
      showToast(err.message || "Failed to load portfolio. Check server.", "error");
      setItems([]);
      refreshDerivedData([]);
    } finally {
      setLoading(false);
    }
  }, [refreshDerivedData, showToast]);

  useEffect(() => { loadPortfolio(); }, [loadPortfolio]);

  const filtered = useMemo(() => {
    return items.filter((item) => {
      const matchCat    = activeCategory === "All" || item.category === activeCategory;
      const matchType   = activeType === "all"     || item.type     === activeType;
      const q           = search.toLowerCase();
      const matchSearch = !q
        || (item.title       || "").toLowerCase().includes(q)
        || (item.category    || "").toLowerCase().includes(q)
        || (item.description || "").toLowerCase().includes(q);
      return matchCat && matchType && matchSearch;
    });
  }, [items, activeCategory, activeType, search]);

  const handleAdd  = () => { setEditItem(null); setFormOpen(true); };
  const handleEdit = (item) => { setEditItem(item); setFormOpen(true); };

  const handleDelete = async (id) => {
    try {
      await apiDeletePortfolio(id);
      const updated = items.filter((item) => item.id !== id);
      setItems(updated);
      refreshDerivedData(updated);
      showToast("Portfolio item deleted.");
    } catch (err) {
      console.error("Delete failed:", err);
      showToast(err.message || "Delete failed. Try again.", "error");
    }
  };

  const handleFormSubmit = async () => {
    setFormOpen(false);
    setEditItem(null);
    await loadPortfolio();
    showToast(editItem ? "Item updated!" : "New item added!");
  };

  const handleFormClose = () => { setFormOpen(false); setEditItem(null); };

  const sidebarCats = [
    { label: "All", count: items.length },
    ...CATEGORIES.map((cat) => ({ label: cat, count: catCounts[cat] || 0 })),
  ];

  return (
    <div className="pa-root">

      <aside className="pa-sidebar">
        <div className="pa-sidebar__logo">
          <span className="pa-sidebar__mark">✦</span>
          <div>
            <p className="pa-sidebar__name">Studio Admin</p>
            <p className="pa-sidebar__sub">Portfolio Manager</p>
          </div>
        </div>

        <p className="pa-sidebar__section-label">Management</p>
        <div className="pa-sidebar__nav-item pa-sidebar__nav-item--active">Portfolio</div>

        <div className="pa-sidebar__cats">
          <p className="pa-sidebar__section-label" style={{ padding: 0, marginBottom: "10px" }}>
            Categories
          </p>
          {sidebarCats.map(({ label, count }) => (
            <button
              key={label}
              className={`pa-sidebar__cat${activeCategory === label ? " pa-sidebar__cat--active" : ""}`}
              onClick={() => setActiveCategory(label)}
            >
              <span className="pa-sidebar__cat-label">{label}</span>
              <span className="pa-sidebar__cat-count">{count}</span>
            </button>
          ))}
        </div>
      </aside>

      <main className="pa-main">

        <div className="pa-topbar">
          <div>
            <p className="pa-topbar__label">Portfolio Management</p>
            <h1 className="pa-topbar__title">
              {activeCategory === "All" ? "All Items" : activeCategory}
            </h1>
          </div>
          <button className="pa-add-btn" onClick={handleAdd}>+ Add Item</button>
        </div>

        <div className="pa-stats">
          {[
            { val: stats.total,    lbl: "Total Items" },
            { val: stats.featured, lbl: "Featured"    },
            { val: stats.images,   lbl: "Images"      },
            { val: stats.videos,   lbl: "Videos"      },
          ].map(({ val, lbl }) => (
            <div className="pa-stat" key={lbl}>
              <div>
                <p className="pa-stat__val">{val}</p>
                <p className="pa-stat__lbl">{lbl}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="pa-filters">
          <div className="pa-search">
            <input
              className="pa-search__input"
              type="text"
              placeholder="Search by title or category..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="pa-tabs">
            {[
              { key: "all",   label: "All Types" },
              { key: "image", label: "Images"    },
              { key: "video", label: "Videos"    },
            ].map(({ key, label }) => (
              <button
                key={key}
                className={`pa-tab${activeType === key ? " pa-tab--active" : ""}`}
                onClick={() => setActiveType(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="pa-empty">
            <p className="pa-empty__title">Loading…</p>
          </div>
        ) : filtered.length > 0 ? (
          <div className="pa-grid">
            {filtered.map((item) => (
              <PortfolioCard
                key={item.id}
                item={item}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        ) : (
          <div className="pa-empty">
            <p className="pa-empty__title">No items found</p>
            <p className="pa-empty__sub">Try adjusting your filters or add a new item.</p>
            <button className="pa-add-btn" style={{ marginTop: "16px" }} onClick={handleAdd}>
              + Add Item
            </button>
          </div>
        )}
      </main>

      <PortfolioForm
        isOpen={formOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        editItem={editItem}
      />

      {toast && (
        <div className={`pa-toast${toast.type === "error" ? " pa-toast--error" : ""}`}>
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default PortfolioAdmin;
